import { api } from "./client";
import type { ApiResponse, PaginatedResponse } from "./types";

export type EventRealm = "community" | "worship";
export type RsvpStatus = "going" | "interested" | "not_going";

export interface EventCategory {
  code: string;
  name: string;
  icon: string | null;
}

export interface EventSummary {
  id: string;
  /** Detail only: the caller's matches going, and how many going share the caller's roots (null below 3). */
  matchesGoingCount?: number;
  sharedRootsCount?: number | null;
  /** An institution or organiser profile carries its live event count and when it joined. */
  organizer?: { name?: string | null; eventCount?: number; since?: string } | null;
  slug: string;
  title: string;
  realm: EventRealm;
  category?: EventCategory | null;
  city: string;
  state: string;
  eventDate: string;
  startTime: string | null;
  isVirtual: boolean;
  isFree: boolean;
  rsvpCount: number;
  maxCapacity: number | null;
  /**
   * The four practical facts that ride on the LIST as well as detail — the
   * ones somebody filters on while scrolling rather than after tapping: can I
   * follow it, can I eat there, what do I wear, will they let me in.
   *
   * The longer three (parking, accessibility, what to bring) are detail-only,
   * where there is room to read them.
   */
  languageCode: string | null;
  ageLimitMin: number | null;
  dressCode: string | null;
  foodInfo: string | null;
  organizerInstitutionId: string | null;
  organizerProviderId: string | null;
  organizerEventOrganizerId: string | null;
  userRsvp?: RsvpStatus | null;
}

/**
 * Everything the event page's "good to know" section draws from.
 *
 * All seven are free text or nullable numbers stated by the organiser and shown
 * verbatim — there is no vocabulary behind them, because "snacks and chai for
 * sale, dinner after the aarti" is not a value any enum could have anticipated.
 */
export interface EventPracticalFacts {
  languageCode: string | null;
  ageLimitMin: number | null;
  dressCode: string | null;
  parkingInfo: string | null;
  foodInfo: string | null;
  accessibility: string | null;
  whatToBring: string | null;
}

export interface ListEventsParams {
  q?: string;
  category?: string;
  city?: string;
  state?: string;
  realm?: EventRealm;
  isVirtual?: boolean;
  isFree?: boolean;
  featured?: "true" | "false";
  page?: number;
  limit?: number;
  sort?: "event_date" | "created_at" | "rsvp_count";
  order?: "asc" | "desc";
}

export function listEvents(params?: ListEventsParams) {
  return api.get<{
    success: true;
    data: { events: EventSummary[]; meta: PaginatedResponse<unknown>["meta"] };
  }>("/events", params as Record<string, string | number | boolean | undefined>);
}

export function getEvent(eventId: string) {
  return api.get<ApiResponse<EventSummary>>(`/events/${eventId}`);
}

export function rsvp(eventId: string, status: RsvpStatus) {
  return api.post<ApiResponse<{ status: RsvpStatus }>>(
    `/events/${eventId}/rsvp`,
    { status },
  );
}

export function removeRsvp(eventId: string) {
  return api.delete<ApiResponse<void>>(`/events/${eventId}/rsvp`);
}

// ═══════════════════════════════════════════════════════════════════
// Organizer lifecycle — create → tiers → publish → check-in → dashboard
// ═══════════════════════════════════════════════════════════════════

export function listCategories() {
  return api.get<ApiResponse<{ categories: EventCategory[] }>>("/events/categories");
}

/** Structured on write and on read. Never flattened to a display string. */
export interface WorshipDetailsInput {
  religion?: string;
  tradition?: string;
  deity?: string;
  festivalCode?: string;
  rituals?: string[];
  dressCode?: string;
  prasadamAvailable?: boolean;
  isRecurringAnnual?: boolean;
  specialInstructions?: string;
  fastingInfo?: string;
}

/**
 * Every writable event fact.
 *
 * The narrow version of this interface listed fourteen fields while the API
 * accepted thirty-five, so anything typed into the rest was accepted by the
 * form and never sent. The list is long because the data path is lossless;
 * a screen still shows only the groups that apply.
 */
export interface CreateEventInput {
  title: string;
  description?: string;
  categoryCode: string;
  tags?: string[];
  relatedStates?: string[];

  eventDate: string; // YYYY-MM-DD
  startTime?: string; // HH:MM wall clock
  endTime?: string;
  timezone?: string;

  venueName?: string;
  address?: string;
  city: string;
  state: string;
  zipCode?: string;
  latitude?: number;
  longitude?: number;
  isVirtual?: boolean;
  virtualUrl?: string;
  isLivestreamed?: boolean;
  livestreamUrl?: string;

  /** The single poster. There is no gallery and no event video. */
  imageUrl?: string;
  externalUrl?: string;

  organizerProviderId?: string;
  organizerInstitutionId?: string;
  organizerEventOrganizerId?: string;
  organizerName?: string;

  maxCapacity?: number;
  /** CANONICAL. Wins over `languageCode` when both are sent. */
  languages?: string[];
  /** DEPRECATED alias of `languages`. */
  languageCode?: string;
  ageLimitMin?: number;
  dressCode?: string;
  parkingInfo?: string;
  foodInfo?: string;
  accessibility?: string;
  whatToBring?: string;
  isFree?: boolean;
  /** Required when `isFree` is false. Prose, never a number. */
  priceInfo?: string;

  /** worship events fan out to institution followers */
  realm?: EventRealm;
  /** Only on a worship event. Sending it on a community event is refused. */
  worshipDetails?: WorshipDetailsInput;
}

/**
 * Creating returns the FULL stored event, not a three-field stub — so the
 * caller can check that every fact it sent was stored without asking again.
 */
export function createEvent(input: CreateEventInput) {
  return api.post<ApiResponse<EventSummary>>("/events", input);
}

/** With `publishAt` (ISO), the event stays a draft and goes live then. */
export function publishEvent(eventId: string, publishAt?: string) {
  return api.post<ApiResponse<{ eventStatus: string; scheduledPublishAt: string | null }>>(
    `/events/${eventId}/publish`,
    publishAt ? { publishAt } : {},
  );
}

/** Back to draft; refused once anybody planned on the event. */
export function unpublishEvent(eventId: string) {
  return api.post<ApiResponse<{ eventStatus: string; alreadyDraft: boolean }>>(`/events/${eventId}/unpublish`);
}

export function cancelScheduledPublish(eventId: string) {
  return api.delete<ApiResponse<{ scheduledPublishAt: null }>>(`/events/${eventId}/publish-schedule`);
}

/** Read only: what publishing would do, before the tap. Organisers only. */
export interface PublishPreview {
  eventStatus: string;
  alreadyPublished: boolean;
  scheduledPublishAt: string | null;
  canPublish: boolean;
  blockers: { code: string; message: string }[];
  willCreateFreeTier: boolean;
  notifies: { audience: string; count: number };
}

export function previewPublish(eventId: string, autoCreateFreeTier = true) {
  return api.get<ApiResponse<PublishPreview>>(`/events/${eventId}/publish-preview`, { autoCreateFreeTier });
}

export function cancelEvent(eventId: string, reason?: string) {
  return api.post<ApiResponse<{ eventStatus: string }>>(`/events/${eventId}/cancel`, { reason });
}

export interface OrganizedEvent {
  id: string;
  title: string;
  slug: string;
  eventStatus: string;
  eventDate: string;
  city: string;
  state: string;
  rsvpCount?: number;
  /** Ask questions no organiser has answered yet. */
  questionsWaiting?: number;
  [key: string]: unknown;
}

export function myOrganizedEvents() {
  return api.get<{
    success: true;
    data: { events: OrganizedEvent[]; meta?: unknown };
  }>("/users/me/organized-events");
}

export function getEventDashboard(eventId: string) {
  return api.get<ApiResponse<Record<string, unknown>>>(`/events/${eventId}/dashboard`);
}

export function checkInTicket(eventId: string, ticketCode: string) {
  return api.post<ApiResponse<Record<string, unknown>>>(`/events/${eventId}/check-in`, {
    ticketCode,
  });
}

export function checkInStats(eventId: string) {
  return api.get<ApiResponse<Record<string, unknown>>>(`/events/${eventId}/check-in-stats`);
}

// ─── Assisted creation (E-1) ────────────────────────────────────────

/**
 * The draft, the review screen and the create request now share ONE field
 * list. Before, somebody could type a dress code, watch the draft come back
 * without it, and have no way to tell whether the parser missed it or the
 * shape could not hold it.
 *
 * Four fields are always null from the parser and present only so the shapes
 * match: `latitude`, `longitude`, `imageUrl` and the organizer ids. A message
 * cannot state a coordinate, a poster is uploaded, and which host an event
 * publishes under is a permission.
 */
export interface EventDraft {
  title: string | null;
  description: string | null;
  categoryCode: string | null;
  eventDate: string | null;
  startTime: string | null;
  endTime: string | null;
  timezone: string | null;
  venueName: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  latitude: number | null;
  longitude: number | null;
  isVirtual: boolean | null;
  virtualUrl: string | null;
  isLivestreamed: boolean | null;
  livestreamUrl: string | null;
  imageUrl: string | null;
  externalUrl: string | null;
  organizerProviderId: string | null;
  organizerInstitutionId: string | null;
  organizerEventOrganizerId: string | null;
  organizerName: string | null;
  tags: string[];
  relatedStates: string[];
  realm: "community" | "worship" | null;
  isFree: boolean | null;
  priceInfo: string | null;
  maxCapacity: number | null;
  languages: string[];
  ageLimitMin: number | null;
  dressCode: string | null;
  parkingInfo: string | null;
  foodInfo: string | null;
  accessibility: string | null;
  whatToBring: string | null;
  worshipDetails: WorshipDetailsInput | null;
}

export interface EventDraftResult {
  preparation: {
    fieldEvidence: Array<{ field: string; evidence: { quote: string; start: number; end: number } }>;
    suggestions: Array<{ code: string; label: string; description: string; evidence: Array<{ quote: string; start: number; end: number }> }>;
    conflicts: Array<{ field: string; kind: string; heldValue: string | null; suggestedValue: string | null; evidence: Array<{ quote: string; start: number; end: number }> }>;
    clarifications: Array<{ field: string; reason: string; question: string }>;
    confirmationReceipt: string | null;
  };
  draft: EventDraft;
  /** GUESSED rather than quoted — highlight. Disjoint from `missing`. */
  uncertain: string[];
  /**
   * What PUBLISH requires and the message did not state. Disjoint from
   * `uncertain`. Optional fields are deliberately absent — listing every empty
   * field put twenty marks on a postable draft and the list stopped meaning
   * anything.
   */
  missing: string[];
  /** model | fallback — open string. */
  source: string;
  fallbackReason: string | null;
  modelVersion: string | null;
}

/**
 * Paste the message, get a draft back. Returns a DRAFT, never a row — post the
 * corrected result to createEvent as normal.
 *
 * `today` must come from the ORGANISER's device: relative dates ("this
 * Saturday") are unresolvable without it, and the server clock would hand
 * someone in a different zone the wrong day.
 */
export function draftEventFromMessage(input: {
  text: string;
  today: string;
  timezone?: string;
  city?: string;
  state?: string;
  /**
   * What the form already holds, on a re-read. The server merges with the rule
   * that a value already held always wins — so a second read cannot undo a
   * hand-typed correction, and cannot clear a poster already uploaded.
   */
  existingDraft?: Record<string, unknown>;
  editedFields?: string[];
}) {
  return api.post<ApiResponse<EventDraftResult>>("/events/draft", input);
}

// ─── The waitlist, and what you already hold ────────────────────────

export interface WaitlistEntry {
  onWaitlist: boolean;
  /** waiting | offered | left — open string. */
  status: string | null;
  /**
   * Where you stand. NULL while holding an offer: you are out of the queue and
   * deciding, not at a number. Also null when not on the list at all.
   */
  position: number | null;
  seats: number;
  waitingCount: number;
  /** When the held seat lapses. Null unless status is "offered". */
  offerExpiresAt: string | null;
}

export interface JoinedWaitlist {
  status: string;
  seats: number;
  position: number | null;
  waitingCount: number;
}

/**
 * Join the queue. `seats` is per PLACE, not per person — a family of four is
 * one entry asking for four.
 */
export function joinWaitlist(eventId: string, seats = 1) {
  return api.post<ApiResponse<JoinedWaitlist>>(`/events/${eventId}/waitlist`, { seats });
}

export function leaveWaitlist(eventId: string) {
  return api.delete<ApiResponse<{ status: string }>>(`/events/${eventId}/waitlist`);
}

export function myWaitlistEntry(eventId: string) {
  return api.get<ApiResponse<WaitlistEntry>>(`/events/${eventId}/waitlist/me`);
}

/**
 * Mirrors `MyPassesData` + `TicketCard` in the spec. Read from the spec, not
 * guessed: the first version of this invented a `tickets[]` field, the real one
 * is `passes[]`, and the page crashed on `.length` of undefined the moment it
 * ran. `docs/api/openapi.json` is the source of truth for every shape here.
 */
export interface TicketCard {
  ticketId: string;
  ticketCode: string;
  status: string;
  attendeeName: string | null;
  tierName: string | null;
  isCheckedIn: boolean;
  checkedInAt: string | null;
}

export interface MyPasses {
  eventId: string;
  /** walk_in | rsvp | ticketed — open string. Separates joining from paying. */
  attendanceMode: string | null;
  passes: TicketCard[];
  passCount: number;
  checkedInCount: number;
  /** going | interested | not_going | null. */
  rsvpStatus: string | null;
  isGoing: boolean;
}

export function myPasses(eventId: string) {
  return api.get<ApiResponse<MyPasses>>(`/events/${eventId}/my-passes`);
}

// ─── Discovery reads that surface the seeded world ──────────────────

export function featuredEvents() {
  return api.get<ApiResponse<{ events: EventSummary[] }>>("/events/featured");
}

export function festivalRail() {
  return api.get<ApiResponse<Record<string, unknown>>>("/events/festival-rail");
}

export function myRsvps() {
  return api.get<ApiResponse<{ events: EventSummary[] }>>("/events/my-rsvps");
}

// ============================================================================
// The organiser's desk, and the two attendee-side reads that hang off an event.
//
// Shapes read from docs/api/openapi.json. Where this file and the spec
// disagree, the spec is right — that rule exists because inventing a response
// shape here once put a blank page in front of every voice.
//
// Two things worth knowing before touching any of it:
//
//   · These are ORGANISER surfaces and they carry attendee EMAIL. Nothing else
//     in the API hands that out. Treat a 403 here as the guard working.
//   · Money is integer CENTS everywhere. Never parse it into a float to
//     display it — divide at the last moment, or don't divide at all.
// ============================================================================

export const UPDATE_TYPES = [
  "general", "alert", "logistics", "photo", "livestream", "cancellation", "rescheduled",
] as const;

export interface EventUpdate {
  id: string;
  eventId: string;
  updateType: string;
  title: string | null;
  body: string;
  imageUrl: string | null;
  isPinned: boolean;
  createdAt: string;
}

export interface EventFeedbackItem {
  id: string;
  overallRating: number;
  comment: string | null;
  highlights: string[];
  improvements: string[];
  isAnonymous: boolean;
  wouldAttendAgain: boolean | null;
  organizerResponse: string | null;
  isFlagged: boolean;
  createdAt: string;
}

export interface FeedbackSummary {
  count: number;
  /** Null when nobody has rated yet — not zero. Zero would read as "bad". */
  avgRating: number | null;
  distribution: Record<string, number>;
}

export interface EventTierRecord {
  id: string;
  name: string;
  description: string | null;
  priceCents: number;
  currency: string;
  /** -1 means unlimited. A paid tier must set a real cap. */
  quantityTotal: number;
  quantitySold: number;
  quantityReserved: number;
  isActive: boolean;
  sortOrder: number;
}

export interface PromoCode {
  id: string;
  code: string;
  type: string;
  percentOff: number | null;
  amountOffCents: number | null;
  tierId: string | null;
  maxRedemptions: number | null;
  maxPerUser: number | null;
  redemptionsCount: number;
  isActive: boolean;
  validFrom: string | null;
  validUntil: string | null;
}

export interface OrganizerAttendee {
  ticketId: string;
  ticketCode: string;
  status: string;
  attendeeName: string | null;
  checkedInAt: string | null;
  tier: { id: string; name: string };
  /** `email` appears ONLY on this organiser surface. */
  user: { id: string; name: string; email: string; avatarUrl: string | null };
  order: { id: string; totalCents: number; currency: string };
}

export interface OrganizerOrder {
  id: string;
  status: string;
  subtotalCents: number;
  discountCents: number;
  totalCents: number;
  currency: string;
  buyer: { name: string; email: string; phone: string | null };
  paidAt: string | null;
  createdAt: string;
  ticketCount: number;
}

// ── attendee side ──────────────────────────────────────────────────

export function listEventRsvps(eventId: string, params?: { status?: string; limit?: number }) {
  return api.get<ApiResponse<{ rsvps: { id: string; status: string; user: { id: string; name: string; city: string | null } }[] }>>(
    `/events/${eventId}/attendees`,
    params as Record<string, string | number | boolean | undefined>,
  );
}

export interface MatchesAttending {
  totalMatchesAttending: number;
  matches: {
    userId: string; name: string; avatarUrl: string | null;
    rsvpStatus: string; matchScore: number | null; matchTier: string | null;
    /** How we know: a mutual wave, or the identity tier. Never merged. */
    via: string;
  }[];
  /** wave_mutuals | mixed | tier_only | empty — say WHICH, never imply certainty. */
  source: string;
  waveCount: number;
  tierFillCount: number;
}

export function getMatchesAttending(eventId: string) {
  return api.get<ApiResponse<MatchesAttending>>(`/events/${eventId}/matches-attending`);
}

/** Save is not Remind: the Lab saves with `false` and asks for a reminder only through the toggle. */
export function addToWatchlist(eventId: string, remindMe: boolean) {
  return api.post<ApiResponse<{ id: string; remindMe: boolean }>>(
    `/events/${eventId}/watchlist`,
    { remindMe },
  );
}

export function removeFromWatchlist(eventId: string) {
  return api.delete<ApiResponse<void>>(`/events/${eventId}/watchlist`);
}

export function setWatchlistReminder(eventId: string, remindMe: boolean) {
  // Separate from add on purpose: watching and being pushed at are two
  // decisions, and muting the reminder must not drop you off the list.
  return api.patch<ApiResponse<{ id: string; remindMe: boolean }>>(
    `/events/${eventId}/watchlist`,
    { remindMe },
  );
}

// ── updates + feedback ─────────────────────────────────────────────

export function listEventUpdates(eventId: string, params?: { limit?: number }) {
  return api.get<ApiResponse<{ items: EventUpdate[] }>>(
    `/events/${eventId}/updates`,
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function createEventUpdate(
  eventId: string,
  input: { body: string; updateType?: string; title?: string; isPinned?: boolean },
) {
  return api.post<ApiResponse<EventUpdate>>(`/events/${eventId}/updates`, input);
}

export function listEventFeedback(eventId: string, params?: { limit?: number }) {
  return api.get<ApiResponse<{ items: EventFeedbackItem[]; summary: FeedbackSummary }>>(
    `/events/${eventId}/feedback`,
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function createEventFeedback(
  eventId: string,
  input: { overallRating: number; comment?: string; isAnonymous?: boolean; wouldAttendAgain?: boolean },
) {
  return api.post<ApiResponse<EventFeedbackItem>>(`/events/${eventId}/feedback`, input);
}

// ── tiers + promo codes ────────────────────────────────────────────

/**
 * What a tier looks like when LISTED, which is not what it looks like when
 * written back.
 *
 * The list says what is LEFT and how it is moving — `quantityAvailable`,
 * `saleState`, `urgencyHint`. It deliberately withholds `quantitySold` and
 * `quantityTotal`; those are the organiser's inventory and appear only on
 * `EventTierRecord`, which the write endpoints return. So a shopper learns
 * "8 left, going fast" and never "42 of 50 sold", which is a different and
 * more revealing fact about how the event is doing.
 *
 * Rendering the write shape against the read endpoint is how this first went
 * out: every number came back undefined and the row read "sold · undefined
 * total" — wrong, with no error to notice.
 */
export interface EventTierListItem {
  id: string;
  name: string;
  description: string | null;
  priceCents: number;
  currency: string;
  /** Null when the tier is unlimited. Not zero — zero means sold out. */
  quantityAvailable: number | null;
  saleState: string;
  /** Null for unlimited tiers, and for any tier not currently on sale. */
  urgencyHint: string | null;
  isActive: boolean;
  sortOrder: number;
}

/**
 * Tiers come from their OWN endpoint, not from the event.
 *
 * `EventDetail` carries no tiers at all. Reading `event.tiers` returns
 * undefined, which renders as "this event is not ticketed" on an event that
 * plainly is — a wrong answer with no error attached, which is the worst kind.
 */
export function listTiers(eventId: string) {
  return api.get<ApiResponse<{ tiers: EventTierListItem[] }>>(`/events/${eventId}/tiers`);
}

/** The organiser's own list: kinds taken off sale too, with the counts. */
export interface EventManagedTier extends EventTierListItem {
  /** -1 is unlimited. */
  quantityTotal: number;
  quantitySold: number;
  quantityReserved: number;
}

export function listManagedTiers(eventId: string) {
  return api.get<ApiResponse<{ tiers: EventManagedTier[] }>>(`/events/${eventId}/tiers/manage`);
}

export function updateTier(eventId: string, tierId: string, input: Partial<{ name: string; priceCents: number; quantityTotal: number; isActive: boolean }>) {
  return api.patch<ApiResponse<EventTierRecord>>(`/events/${eventId}/tiers/${tierId}`, input);
}

export function deleteTier(eventId: string, tierId: string) {
  return api.delete<ApiResponse<void>>(`/events/${eventId}/tiers/${tierId}`);
}

export function listPromoCodes(eventId: string) {
  return api.get<ApiResponse<{ promoCodes: PromoCode[] }>>(`/events/${eventId}/promo-codes`);
}

export function createPromoCode(
  eventId: string,
  input: { code: string; type: "percent_off" | "amount_off"; percentOff?: number; amountOffCents?: number; maxRedemptions?: number },
) {
  return api.post<ApiResponse<PromoCode>>(`/events/${eventId}/promo-codes`, input);
}

export function updatePromoCode(eventId: string, promoCodeId: string, input: Partial<{ isActive: boolean; maxRedemptions: number }>) {
  return api.patch<ApiResponse<PromoCode>>(`/events/${eventId}/promo-codes/${promoCodeId}`, input);
}

export function deletePromoCode(eventId: string, promoCodeId: string) {
  return api.delete<ApiResponse<void>>(`/events/${eventId}/promo-codes/${promoCodeId}`);
}

export function validatePromoCode(
  eventId: string,
  code: string,
  items: { tierId: string; quantity: number }[],
) {
  // Returns `valid` plus a `reason` when not. The reason is the product —
  // "expired" and "already used" need different words on screen.
  return api.post<ApiResponse<{ valid: boolean; code: string; discountCents: number; reason: string | null }>>(
    `/events/${eventId}/promo-codes/validate`,
    { code, items },
  );
}

// ── the desk ───────────────────────────────────────────────────────

export function rescheduleEvent(
  eventId: string,
  input: { eventDate: string; startTime?: string; endTime?: string; reason?: string },
) {
  // Answers with how many people were TOLD. A reschedule nobody heard about
  // is the failure this number exists to make visible.
  return api.post<ApiResponse<{ eventId: string; eventDate: string; startTime: string | null; recipientsNotified: number }>>(
    `/events/${eventId}/reschedule`,
    input,
  );
}

/** The page facts both owner lists send beside `data`: total is the server's count, never the page length. */
export interface OwnerListMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export function listOrganizerAttendees(eventId: string, params?: { limit?: number; page?: number }) {
  return api.get<ApiResponse<{ attendees: OrganizerAttendee[] }> & { meta: OwnerListMeta }>(
    `/events/${eventId}/dashboard/attendees`,
    params as Record<string, string | number | boolean | undefined>,
  );
}

/** GET /events/:id/dashboard/plates: plates per diet, counts only (a diet under 3 folds into other). */
export interface EventPlates {
  eventId: string;
  plates: number;
  byDiet: Array<{ diet: string; label: string; count: number }>;
  other: number;
  otherIncludes: Array<{ diet: string; label: string }>;
  floor: number;
}

export function getEventPlates(eventId: string) {
  return api.get<ApiResponse<EventPlates>>(`/events/${eventId}/dashboard/plates`);
}

export function listOrganizerOrders(eventId: string, params?: { limit?: number; page?: number }) {
  return api.get<ApiResponse<{ orders: OrganizerOrder[] }> & { meta: OwnerListMeta }>(
    `/events/${eventId}/dashboard/orders`,
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function refundOrder(
  eventId: string,
  orderId: string,
  input?: { ticketIds?: string[]; reason?: string },
) {
  // Omitting ticketIds refunds the WHOLE order; naming them refunds those
  // seats only. `isFullRefund` on the reply says which actually happened.
  return api.post<ApiResponse<{ refundId: string; amountCents: number; isFullRefund: boolean; status: string }>>(
    `/events/${eventId}/orders/${orderId}/refund`,
    input ?? {},
  );
}

/** Edit an event: send only what changes. */
export function updateEvent(eventId: string, change: Record<string, unknown>) {
  return api.patch<ApiResponse<{ id: string }>>(`/events/${eventId}`, change);
}

/** One edition of a series, newest first (GET /events/{id}/editions). */
export interface EventEdition {
  id: string;
  slug: string;
  title: string;
  eventDate: string;
  editionNumber: number | null;
  eventStatus: string;
  wentCount: number;
}

export function listEditions(eventId: string) {
  return api.get<ApiResponse<{ editions: EventEdition[] }>>(`/events/${eventId}/editions`);
}
